import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ReactHtml from 'raw-html-react';
import useSWR from 'swr';

import BreadCrumbs from 'components/Common/BreadCrumbs/BreadCrumbs';
import PageNotFound from 'components/Common/Errors/PageNotFound/PageNotFound';
import Loader from 'components/Common/Loader/Loader';

import { newHierarchy } from 'services/data';
import { fetcher } from 'services/fetcher';
import { replaceByLinkEnv } from 'services/formatter';

import { GTM_PAGE_OTHER } from 'settings/gtm/gtmPageView';

import useGtmPageView from 'hooks/gtm/useGtmPageView';
import useEnv from 'hooks/useEnv';

import { FetchError } from 'exceptions/FetchError';

import styles from './CmsPage.module.scss';

function CmsPage(): JSX.Element {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { urlApiCms } = useEnv();
  const formattedPathName = pathname.split('/')[2];
  const apiPath = `${urlApiCms}/content/get/${formattedPathName}`;

  const { data, error, isValidating } = useSWR<CmsContent>(apiPath, fetcher, {
    revalidateOnFocus: false,
    onErrorRetry: (
      error: FetchError,
      _key,
      config,
      revalidate,
      { retryCount }
    ) => {
      if (error.status === 404) {
        return;
      }

      setTimeout(() => revalidate({ retryCount }), config.errorRetryInterval);
    },
  });

  const pageTitle =
    data?.metadata?.title || (t('DEFAULT_HEAD_TITLE') as string);
  useGtmPageView(GTM_PAGE_OTHER, pageTitle, {
    isLoaded: (!!data || !!error) && !isValidating,
  });

  if (error) {
    return <PageNotFound />;
  } else if (!data) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={
            data?.metadata?.metaDescription ||
            t('DEFAULT_HEAD_META_DESCRIPTION')
          }
        />
      </Helmet>
      <BreadCrumbs
        className={styles.breadcrumbs}
        breadCrumbsHierarchies={[
          newHierarchy('/', t('LABEL_HOME')),
          newHierarchy('', `${data.label}`),
        ]}
      />
      <ReactHtml html={replaceByLinkEnv(data.html)} />
    </>
  );
}

export default CmsPage;

type CmsContent = {
  html: string;
  label: string;
  metadata?: {
    title?: string;
    metaDescription?: string;
  };
};
